import { useState, useEffect, useRef } from 'react';
import { MailCheck, Loader2, X, CheckCircle2, AlertCircle, LogIn } from 'lucide-react';

/**
 * Подтверждение email по ссылке из письма (BUG-AUTH-01, FR-AUTH-22).
 * Письмо ведёт на фронтовый /verify?token=… — SPA без роутера ловит токен
 * из window.location в App.jsx (так же, как сброс пароля, FR-AUTH-19).
 *
 * При монтировании с токеном вызывает POST /auth/verify-email {token} РОВНО ОДИН РАЗ
 * (startedRef-guard защищает от двойного монтирования эффекта в React StrictMode).
 * Состояния: loading → success | used | error. Спиннер всегда завершается в
 * терминальное состояние — резолв запроса фиксируется даже после StrictMode-ремаунта
 * (мы намеренно НЕ отменяем/не игнорируем результат в cleanup, иначе спиннер зависал бы).
 *
 * props:
 *   token              — токен из ссылки письма (?token=…)
 *   onVerify(token)    — async (auth.verifyEmail), бросает Error с .code при ошибке
 *   onClose()          — закрыть оверлей
 *   onSwitchMode(mode) — переключить режим модалки ('login' | 'register')
 */
const STATUS = { LOADING: 'loading', SUCCESS: 'success', USED: 'used', ERROR: 'error' };

export default function VerifyEmailModal({ token, onVerify, onClose, onSwitchMode }) {
  const [status, setStatus] = useState(STATUS.LOADING);
  // Запрос подтверждения должен уйти ровно один раз на токен.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    if (!token) {
      setStatus(STATUS.ERROR);
      return;
    }

    (async () => {
      try {
        await onVerify(token);
        setStatus(STATUS.SUCCESS);
      } catch (err) {
        const code = err?.code;
        // TOKEN_USED трактуем как фактический успех: аккаунт уже подтверждён
        // (например, из-за повторного вызова при StrictMode-ремаунте в dev).
        if (code === 'TOKEN_USED') {
          setStatus(STATUS.USED);
        } else {
          // INVALID_TOKEN | TOKEN_EXPIRED | SERVICE_UNAVAILABLE | прочее.
          setStatus(STATUS.ERROR);
        }
      }
    })();
    // Без cleanup-флага: на StrictMode-ремаунте startedRef уже true (повторный
    // запрос не уйдёт), а резолв первого вызова спокойно зафиксирует статус.
  }, [token, onVerify]);

  const verified = status === STATUS.SUCCESS || status === STATUS.USED;

  return (
    <div className="acc-modal" role="dialog" aria-modal="true" aria-label="Подтверждение email">
      <div className="acc-modal-head">
        <h2><MailCheck size={16} /> Подтверждение email</h2>
        <button className="acc-icon-btn" onClick={onClose} aria-label="Закрыть">
          <X size={16} />
        </button>
      </div>

      <div className="acc-body">
        {status === STATUS.LOADING && (
          <div className="acc-loading"><Loader2 size={18} className="spin" /> Подтверждаем email…</div>
        )}

        {verified && (
          <>
            <div className="acc-success" role="status">
              <CheckCircle2 size={16} />
              <span>
                {status === STATUS.SUCCESS
                  ? 'Email успешно подтверждён. Теперь вы можете войти.'
                  : 'Email уже подтверждён. Теперь вы можете войти.'}
              </span>
            </div>
            <button className="acc-btn-primary" type="button" onClick={() => onSwitchMode?.('login')}>
              <LogIn size={14} /> Войти
            </button>
          </>
        )}

        {status === STATUS.ERROR && (
          <>
            <div className="acc-error" role="alert">
              <AlertCircle size={14} /> Ссылка недействительна или устарела, запросите подтверждение заново.
            </div>
            <div className="acc-links">
              <button type="button" className="acc-link" onClick={() => onSwitchMode?.('register')}>
                Зарегистрироваться заново
              </button>
              <button type="button" className="acc-link" onClick={() => onSwitchMode?.('login')}>
                Войти
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
